// Central content for London Biotech International Limited landing page.
// Team / Partners / Project entries are professional placeholders — replace with
// real names, bios, logos and project details when provided.

export const IMAGES = {
  heroDna:
    'https://images.unsplash.com/photo-1633167606207-d840b5070fc2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600',
  lab:
    'https://images.unsplash.com/photo-1602052577122-f73b9710adba?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400',
  concept:
    'https://images.unsplash.com/photo-1712873068978-eb0cb20d1b62?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600',
  project:
    'https://images.unsplash.com/photo-1587351021821-f871837248c6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600',
  architecture:
    'https://images.unsplash.com/photo-1626315869436-d6781ba69d6e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400',
  gov:
    'https://images.unsplash.com/photo-1514557718210-26e452f8fab0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600',
  labWide:
    'https://images.unsplash.com/photo-1614935151651-0bea6508db6b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400',
  hospital2:
    'https://images.unsplash.com/photo-1626315869436-d6781ba69d6e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400',
};

export const NAV_LINKS = [
  { id: 'welcome', label: 'Welcome' },
  { id: 'about', label: 'About Us' },
  { id: 'what-we-do', label: 'What We Do' },
  { id: 'expertise', label: 'Our Expertise' },
  { id: 'concept', label: 'The Concept' },
  { id: 'government', label: 'Government Partnerships' },
  { id: 'team', label: 'Our Team' },
  { id: 'partners', label: 'Our Partners' },
  { id: 'project', label: 'The Project' },
  { id: 'contact', label: 'Contact' },
];

// A tighter set for the desktop bar.
export const DESKTOP_NAV = [
  { id: 'about', label: 'About' },
  { id: 'expertise', label: 'Expertise' },
  { id: 'concept', label: 'Concept' },
  { id: 'government', label: 'Partnerships' },
  { id: 'team', label: 'Team' },
  { id: 'project', label: 'Project' },
];

export const EXPERTISE = [
  {
    no: '01',
    title: 'Masterplanning',
    desc:
      'Strategic spatial planning for integrated life-science districts, campuses and health precincts — from feasibility to phased delivery.',
  },
  {
    no: '02',
    title: 'Legal Frameworks',
    desc:
      'Structuring the special-purpose vehicles, contractual instruments and cross-border governance that underpin complex infrastructure.',
  },
  {
    no: '03',
    title: 'Healthcare Consultancy',
    desc:
      'Clinical service planning, operational modelling and care-pathway design that align facilities with real-world health outcomes.',
  },
  {
    no: '04',
    title: 'Life Sciences Delivery',
    desc:
      'End-to-end project delivery for laboratories, research institutes and biomanufacturing — engineered to international standards.',
  },
];

export const SERVICES = [
  {
    title: 'Infrastructure Development',
    desc:
      'We conceive and deliver the physical assets that host discovery — research campuses, hospitals and biomanufacturing hubs.',
  },
  {
    title: 'Investment Structuring',
    desc:
      'As a special-purpose vehicle we ring-fence and de-risk capital, coordinating institutional investors and public stakeholders.',
  },
  {
    title: 'Public–Private Coordination',
    desc:
      'We convene governments, universities and industry into a single, accountable delivery framework with a shared mandate.',
  },
  {
    title: 'Advisory & Governance',
    desc:
      'Independent oversight, legal architecture and clinical advisory keep every project compliant, transparent and outcome-led.',
  },
];

export const TEAM_INTRO =
  'A multidisciplinary leadership team with global experience, strategic vision and a shared commitment to build world-class healthcare and biotechnology ecosystems.';

export const DIRECTORS = [
  {
    name: 'Mr Patrick Newman',
    title: 'Director',
    initials: 'PN',
    photo: 'patrick',
    summary:
      'Mr Patrick Newman brings over 25 years of experience in emerging markets of Africa and Central Asia, with a strong focus on mining, food security and energy, including renewables.',
    highlights: [
      'Negotiated mining licences and agreements in the Republic of Sudan, Kenya, Ghana and Côte d\u2019Ivoire (including with AfricaRice — an organisation owned by 28 African Governments).',
      'Business development in Nigeria delivering UK technology in carbon-neutral, solar-powered water desalination as part of water-security solutions.',
      'Established track record in Uzbekistan and Kazakhstan with a focus on the mining sector.',
    ],
    expertise: [
      'Mining & Natural Resources',
      'Food Security',
      'Energy & Renewables',
      'Government & Institutional Relations',
      'International Negotiations',
      'Business Development',
      'Emerging Markets Strategy',
    ],
  },
  {
    name: 'Mr Pradeep Venkataramakrishna',
    title: 'Director',
    initials: 'PV',
    photo: 'pradeep',
    summary:
      'Mr Pradeep Venkataramakrishna is an international business, investment and strategy professional with experience across banking, financial services, investment advisory, product strategy and venture development.',
    highlights: [
      'Senior leadership roles at IDFC FIRST Bank, HDFC Bank, YES BANK and ICICI Bank across private banking, corporate banking, wealth management and structured finance.',
      'Advisory on a US$350 million acquisition of a portfolio of luxury five-star resorts in South Asia.',
      'Managing Director, Mars Mining Limited — leading critical-minerals strategy and international projects.',
      'Leading acquisition of a chain of gold mines in Morocco through Anglo Arab Morocco Gold Capital Limited (SPV).',
    ],
    expertise: [
      'Investment & Strategy',
      'Corporate Finance',
      'Venture Development',
      'Mining & Critical Minerals',
      'Healthcare & Biotechnology Ecosystems',
      'AI & Technology Platforms',
      'Capital Structuring',
      'Global Partnerships',
    ],
  },
];

export const TEAM_PILLARS = [
  {
    title: 'Global Perspective',
    text: 'Decades of international experience across Africa, Europe, Asia and the Middle East.',
    icon: 'globe',
  },
  {
    title: 'Complementary Strengths',
    text: 'Deep domain expertise in resources, finance, strategy and ecosystem development.',
    icon: 'handshake',
  },
  {
    title: 'Strategic Partnerships',
    text: 'Strong networks with governments, institutions, investors and global industry leaders.',
    icon: 'network',
  },
  {
    title: 'Shared Vision',
    text: 'Building world-class healthcare and biotech destinations that transform lives and economies.',
    icon: 'vision',
  },
];

export const LEAD8 = {
  name: 'Lead8',
  tagline: 'Design. Transform. Impact.',
  headline: 'Global track record & award-winning design.',
  description:
    "Lead8 has delivered many of Asia\u2019s most iconic and award-winning developments across diverse sectors and geographies \u2014 creating value, shaping communities and enhancing lives.",
  facts: [
    { value: '20+', label: 'Countries across Asia, Europe & the Middle East' },
    { value: '150+', label: 'Architects, planners, designers & strategists' },
    { value: '200+', label: 'Projects: mixed-use, healthcare, commercial & urban' },
    { value: '100+', label: 'International design awards' },
    { value: '25+', label: 'Years of design leadership & innovation' },
  ],
  projects: [
    { name: 'Penang International Commercial City', place: 'Penang, Malaysia' },
    { name: 'Nansha International Financial Forum', place: 'Guangzhou, China' },
    { name: 'CIMB Leadership Academy', place: 'Kuala Lumpur, Malaysia' },
    { name: 'Heydar Aliyev Centre', place: 'Baku, Azerbaijan' },
    { name: 'K11 Artus', place: 'Hong Kong' },
  ],
  differentiators: [
    'Integrated design thinking across scales & sectors',
    'Human-centred & future-ready placemaking',
    'Sustainability & ESG embedded in every project',
    'Collaborative approach with governments & partners',
    'Delivering economic, social & environmental value',
  ],
  trustedBy: [
    'CIMB', 'UEM Sunrise', 'Samsung', 'Malaysia Airports', 'CapitaLand',
    'Sunway', 'Hongkong Land', 'Emaar', 'GIC', 'Government of Malaysia',
  ],
  closing:
    'Lead8 creates destinations that inspire, connect and endure \u2014 delivering value for investors, communities and generations.',
};

export const MCCARTHY = {
  name: 'McCarthy Denning',
  sector: 'Legal Advisors \u2014 UK',
  tagline: 'Leading legal guidance. Structured for success.',
  person: 'Richard Beresford',
  role: 'Partner & Head of International, McCarthy Denning LLP',
  description:
    'McCarthy Denning is a top-tier UK law firm with deep expertise in complex international transactions, infrastructure, real estate and public-private partnerships. Richard Beresford, Partner and Head of International, brings over 30 years of experience advising governments, developers and investors on high-value, cross-border projects.',
  mandate:
    'For the Global Healthcare City programme, McCarthy Denning will provide end-to-end legal advisory across project structuring, funding, risk allocation, contracts and regulatory compliance \u2014 ensuring a robust legal foundation aligned with international best practices.',
  credentials: [
    { value: 'Top 200', label: 'UK law firm*' },
    { value: '220+', label: 'Lawyers' },
    { value: '50+', label: 'Countries advised' },
    { value: '30+', label: "Years' experience" },
    { value: 'Cross-border', label: 'High-value expertise' },
  ],
  expertise: [
    { icon: 'infra', title: 'Project & Infrastructure', text: 'Legal structuring, PPP models, risk allocation and contractual frameworks.' },
    { icon: 'finance', title: 'Project Finance & Funding', text: 'Debt & equity finance, security packages, funding documentation and lender advisory.' },
    { icon: 'corporate', title: 'Corporate & Investment', text: 'Joint ventures, M&A, shareholders\u2019 agreements and investment structuring.' },
    { icon: 'realestate', title: 'Real Estate & Development', text: 'Land acquisitions, developments, agreements, leasing and asset management.' },
    { icon: 'regulatory', title: 'Regulatory & Compliance', text: 'Regulatory approvals, planning, environmental compliance and governance.' },
    { icon: 'risk', title: 'Risk Management & Dispute Resolution', text: 'Risk mitigation strategies, contract management and dispute resolution support.' },
    { icon: 'international', title: 'International Transactions', text: 'Cross-border advisory, multijurisdictional structuring and documentation.' },
    { icon: 'publiclaw', title: 'Public Law & Procurement', text: 'Public procurement, concessions and public sector legal advisory.' },
  ],
  firm: [
    'Top 200 UK law firm with a strong international reputation.',
    'Ranked in Legal 500 and Chambers & Partners for key practice areas.',
    'Over 220 lawyers across London, Cambridge and Oxford.',
    'Clients include governments, global corporations, investors and financial institutions.',
  ],
  quote:
    'Our role is to mitigate risk, enable opportunity and deliver legal clarity at every stage of this transformative project.',
  note: '*The Times Best Law Firms 2024',
  closing: 'Expert legal advice. Stronger foundations.',
};

export const MARQUEE_ITEMS = [
  'Public Sector Collaboration',
  'International Reach',
  'Sovereign Health Programmes',
  'Institutional Partnership',
  'Cross-Border Delivery',
];

export const PROJECT = {
  intro: [
    'Global Healthcare City is a visionary integrated ecosystem designed to bring together world-class healthcare, biotechnology, life sciences, artificial intelligence, medical research, education and innovation within a single destination.',
    'Developed through strategic Public\u2013Private Partnerships (PPP), the project fosters collaboration between governments, hospitals, universities, research institutions, technology companies and global investors. By connecting clinical excellence, scientific research and advanced technologies, it accelerates innovation, strengthens healthcare infrastructure, creates high-skilled employment and improves access to world-class healthcare.',
    'As a globally connected health and innovation ecosystem, the project is designed to transform scientific discoveries into real-world healthcare solutions \u2014 driving sustainable economic growth and improving lives across the world.',
  ],
  flagship: {
    name: 'A self-sustaining health & innovation ecosystem',
    tagline: 'A future of health. A legacy for generations.',
    lead:
      'A strategically located, world-class development \u2014 combining advanced infrastructure, a thriving innovation ecosystem, exceptional talent and progressive policy to build a global health & biotech city.',
    stats: [
      { value: '500', unit: 'Acres', label: 'Integrated development' },
      { value: 'US$1.27', unit: 'Billion', label: 'Total investment, all phases' },
      { value: '4,000', unit: 'Beds', label: '3,000 beds in Phase 1' },
      { value: '4', unit: 'Phases', label: '30-year concession model' },
      { value: '50,000+', unit: 'Jobs', label: 'Direct & indirect' },
      { value: 'Global', unit: 'Partners', label: 'Design, healthcare & innovation' },
    ],
    ecosystem: [
      { icon: 'excellence', title: 'Healthcare Excellence', text: 'Tertiary, quaternary and super-speciality services.' },
      { icon: 'biotech', title: 'Biotech & Life Sciences', text: 'Advanced research, drug discovery, vaccines, genomics and medtech.' },
      { icon: 'education', title: 'Education & Training', text: 'Medical, nursing, allied sciences and continuous professional development.' },
      { icon: 'wellness', title: 'Wellness & Preventive Care', text: 'Preventive health, wellness resorts, rehab and holistic well-being.' },
      { icon: 'tourism', title: 'Medical Tourism & Global Reach', text: 'International patient care, long-stay facilities and global partnerships.' },
      { icon: 'smart', title: 'Sustainable & Smart City', text: 'Green infrastructure, renewable energy, digital systems and smart mobility.' },
    ],
    components: [
      'Super Speciality Hospital',
      'Medical College & Teaching Hospital',
      'Biotech & Innovatech Park',
      'Life Sciences Research Hub',
      'Nursing College & Allied Health Sciences',
      'Hotel & International Hospitality',
      'Residential Township & Serviced Apartments',
    ],
    closing:
      'Together, building a global health & innovation powerhouse that drives economic growth and improves lives.',
  },
};
